import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import {
    pdfDefaultOptions,
    PdfLoadedEvent,
    TextLayerRenderedEvent
} from 'ngx-extended-pdf-viewer';

import {
    ClassifyPdfSelectionDialogComponent
} from '../classify-pdf-selection-dialog/classify-pdf-selection-dialog.component';
import { DocumentPreviewComponent } from '../document-preview/document-preview.component';
import { ClassifySelectionResult, PageTextDiv, SelectedLine, TextDiv } from '../document-text-select/document-text-select.models';
import { DocumentTextSelectService } from '../document-text-select/document-text-select.service';

@Component({
    selector: 'pdf-document-preview',
    templateUrl: './pdf-document-preview.component.html',
    styleUrls: ['./pdf-document-preview.component.scss'],
    standalone: false
})
export class PdfDocumentPreviewComponent extends DocumentPreviewComponent implements OnInit, AfterViewInit {

    private _statementFileUrl?: string;
    @Input()
    set statementFileUrl(temp: any) {
        console.log('temp:', temp);
        if (temp) {
            this._statementFileUrl = temp;
            this.loadPdfDoc();
        }
    }
    get statementFileUrl(): any { return this._statementFileUrl; }

    @Input() isSelfServiceReview!: boolean;

    @Output() onPdfClassify: EventEmitter<any> = new EventEmitter<any>();

    //////////////////////////////////////////////////////////////////////////
    // PDF Document Variables
    //////////////////////////////////////////////////////////////////////////
    @ViewChild('pdfViewerWrapper', { static: false }) pdfViewerWrapper!: ElementRef; // pdf view wrap

    documentTextSelectService!: DocumentTextSelectService;

    public pdfLoading = true;
    public pdfLoadedProgress = 0;
    public pdfCurrentPage = 1;
    public pdfTotalPages = 1;
    public pdfTextHighlighted = false;
    public pdfPgTextDivs: PageTextDiv[] = [];
    public pdfPgHighlightedFeeLines: Array<SelectedLine> = [];
    pdfZoomInputValue = '100%';

    private _pdfZoom = 100;
    public get pdfZoom(): number { return this._pdfZoom; }
    public set pdfZoom(value: number) {
        this._pdfZoom = Math.round(value * 1e6) / 1e6;
    }

    //////////////////////////////////////////////////////////////////////////
    // Used the following as a reference for embedding the PDF viewer:
    // https://www.npmjs.com/package/ngx-extended-pdf-viewer
    //////////////////////////////////////////////////////////////////////////
    // ngx-extended-pdf-viewer attributes
    // Documentation here: https://pdfviewer.net/extended-pdf-viewer/attributes
    //////////////////////////////////////////////////////////////////////////
    public pdfSrc = '';                        // defines the URL of the PDF file
    public pdfBase64Src = '';                          // accepts a PDF file as a base64 encoded string
    public pdfRotation = 0;                    // Start at square-one
    public pdfDefaultOptions = {
        cursorToolOnLoad: 0,
        defaultZoomValue: 100,
        externalLinkTarget: 0,
        renderer: 'canvas',
        // assetsFolder: 'assets/ngx-extended-pdf-viewer/',
        assetsFolder: 'assets/',
        workerSrc: `${pdfDefaultOptions.assetsFolder}pdf.worker.js`,
        // To be reviewed
        enableWebGL: false,
        maxCanvasPixels: 16777216,
        rangeChunkSize: 65536,
        removePageBorders: false,
        enableXfa: true,
        scrollModeOnLoad: -1,
        spreadModeOnLoad: -1,
        textLayerMode: 2,
        useOnlyCssZoom: false,
        viewOnLoad: 0,
        disableRange: false,
        maxImageSize: -1,
        doubleTapZoomFactor: 'page-width',
        enableScripting: true,
        defaultCacheSize: 50
    };

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super();

        if (typeof window !== 'undefined') {
            if ((<any>window).pdfDefaultOptions) {
                this.pdfDefaultOptions = (<any>window).pdfDefaultOptions;
            } else {
                (<any>window).pdfDefaultOptions = this.pdfDefaultOptions;
            }
        }
    }

    ngOnInit(): void {
    }

    ngAfterViewInit(): void {
        this.documentTextSelectService = new DocumentTextSelectService(this.pdfViewerWrapper, this, false);
    }

    loadPdfDoc(): void {
        this.pdfLoading = true;     // This will = false when TotalPages is updated
        if (this.statementFileUrl !== 'undefined') {
            //   this.pdfBase64Src = this.statementFileUrl; // this is the base64 encoded string
            this.pdfSrc = this.statementFileUrl;
            if (this.pdfTotalPages > 0) {
                this.pdfLoading = false;
            }
            this.changeDetectorRef.detectChanges();
        }
    }

    pdfFormatLabel(value: number): string {
        return `${value.toString()}%`;
    }

    // Rotation Buttons
    pdfRotateLeft(): void {
        this.pdfRotation = Math.abs(((this.pdfRotation -= 90) + 360) % 360);
        this.changeDetectorRef.detectChanges();
    }

    pdfRotateRight(): void {
        this.pdfRotation = (this.pdfRotation += 90) % 360;
        this.changeDetectorRef.detectChanges();
    }

    // Zoom Control
    onPdfSliderChange(event: any): void {
        // Zoom Slider Bubble Text
        if (typeof (event.value) === 'number') {
            this.pdfZoom = Math.round(event.value);
            // this.pdfCurrentZoomFactor = Math.round(event.value / 10) / 10;
        } else if (typeof (event.value) === 'string') {
            this.pdfZoom = event.value;
        }
        // If highlighted text exist, repaint the selected text
        this.changeDetectorRef.detectChanges();
    }

    //////////////////////////////////////////////////////////////////////////
    // PDF Viewer Event Functions
    //////////////////////////////////////////////////////////////////////////
    onPdfLoaded(pg: PdfLoadedEvent): void {
        this.pdfTotalPages = pg.pagesCount;
        this.pdfLoading = false;
        for (let i = 0; i < pg.pagesCount; i++) {
            this.initTextSelected(i + 1, []);
        }
        setTimeout(() => this.fitPdfToWidth(), 100);
        this.changeDetectorRef.detectChanges();
    }

    fitPdfToWidth(): void {
        const containerWidth = this.pdfViewerWrapper?.nativeElement?.clientWidth ?? 0;
        const canvas = this.pdfViewerWrapper?.nativeElement?.querySelector('canvas');
        if (containerWidth > 0 && canvas) {
            const currentZoom = (this.pdfZoom as number) || 100;
            const actualPageWidthAtCurrentZoom = canvas.clientWidth;
            const nativePageWidth = actualPageWidthAtCurrentZoom / (currentZoom / 100);
            const rawZoomPercent = (containerWidth / nativePageWidth) * 100;
            const roundedZoom = Math.floor(rawZoomPercent / 10) * 10; // round DOWN to nearest 10 so it never overflows
            this.pdfZoom = Math.min(Math.max(roundedZoom, 20), 400);
        }
        this.changeDetectorRef.detectChanges();
    }

    onPdfLoadingFailed(event: any): void {
        this.pdfTotalPages = event?._pdfInfo?.numPages;
        this.pdfLoading = false;
    }

    onPdfProgress(event: any): void {
        switch (event.type) {
            case 'print':
                break;
            default:
                // This updates the progress bar above the pdf document when loading
                this.pdfLoadedProgress = Math.round(event.percent);
                break;
        }
    }

    onPdfTextLayerRendered(event: TextLayerRenderedEvent): void {
        const textLayer = event.source.textLayer;
        if (textLayer != null && (textLayer.textContentItemsStr?.length ?? 0) > 0) {
            this.initTextSelected(event.pageNumber, textLayer.textDivs);
        }

        // only set format once (only needs first page)
        if (event.pageNumber === 1 && this.pdfPgTextDivs[0]?.textDivs)
            this.documentTextSelectService.setProcessorAndFormat(this.pdfPgTextDivs[0].textDivs);
    }

    // Initialize the collection of selected text
    // Pass in just the page number to initialize the page
    // Pass in the page number and the text divs to initialize the page collection
    initTextSelected(pgNum: number, elements: Element[]) {
        if (pgNum === 0) return;
        const divCnt = elements.length;
        if (divCnt === 0) {
            const pgObj = {} as PageTextDiv;
            pgObj.page = pgNum;
            pgObj.textDivs = [];
            this.pdfPgTextDivs.push(pgObj);
        } else {
            while (this.pdfPgTextDivs.length < pgNum) {
                this.pdfPgTextDivs.push({ page: this.pdfPgTextDivs.length + 1, textDivs: [] });
            }
            this.pdfPgTextDivs[pgNum - 1].textDivs = [];
            for (let d = 0; d < divCnt; d++) {
                const txtObj = {} as TextDiv;
                txtObj.selected = false;
                txtObj.el = elements[d];
                this.pdfPgTextDivs[pgNum - 1].textDivs.push(txtObj);
            }
        }
        if (this.pdfTextHighlighted) {
            this.pdfPgHighlightedFeeLines.forEach(({ pg, startTd, selCnt }) => this.updateSelectedTextDivs(pg, startTd, selCnt));
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    // Launch the ClassifyPdfSelection modal dialog
    ////////////////////////////////////////////////////////////////////////////
    // There are two ways of implementing this dialog box and providing the
    //   needed ClassifyPdfSelectionResult[] data:
    // 1. Pass the selected text into this call as a parameter (passedInData)
    // 2. Get the selected text info from a service (serviceData) after this
    //    method has been called (from within)
    openClassifyPdfFees(passedInData?: ClassifySelectionResult[]) {
        if (!passedInData) return;

        const dialogRef = this.dialog.open(ClassifyPdfSelectionDialogComponent, {
            data: { passedInData: passedInData },
            panelClass: 'pretty-dialog',
            backdropClass: 'classify-dialog-passthrough-backdrop',
            autoFocus: true,
            restoreFocus: true,
            disableClose: true,
            height: '80vh',
        });

        dialogRef.afterClosed().subscribe((pdfFeeClassifications: Array<ClassifySelectionResult>) => {
            if (pdfFeeClassifications?.length > 0) {
                // this.processPdfFeeClassifications(pdfFeeClassifications);
                this.onPdfClassify.emit(pdfFeeClassifications);

                this.analyzeSelectedText(pdfFeeClassifications); // can i send in selected nodes here?
            }

            // Now that we've shared the text, let's clear the current selection.
            document.getSelection()?.removeAllRanges();
        });
    }

    analyzeSelectedText(pdfFeeClassifications: Array<ClassifySelectionResult>) {
        pdfFeeClassifications.forEach(feeLine => {

            //////////////////////////////////////////////////////////////////////////
            // STEP 1: Construct the SearchText string to search through all nodes
            //         Starting with the first textDiv selected for that text
            //         throughout all of the text divs on that page
            //////////////////////////////////////////////////////////////////////////
            let searchText = '';
            feeLine.htmlElements.forEach(thisNode => {
                searchText += thisNode.innerHTML;
            });

            //////////////////////////////////////////////////////////////////////////
            // STEP 2: Search the list of nodes on the current page and try to find
            //         the numerical position (or address) of that searchText on the
            //         page.  Once found, update each textDiv's selected status.
            //////////////////////////////////////////////////////////////////////////
            let foundIt = false;
            let start = 0;
            const p = this.pdfCurrentPage;
            for (let t = 0; t < this.pdfPgTextDivs[p - 1].textDivs.length; t++) {
                // if the first selected node equals the textDiv on this page, we MIGHT have found it
                // We'll grab the whole first line to verify that.
                if (this.pdfPgTextDivs[p - 1].textDivs[t].el.innerHTML === feeLine.htmlElements[0].innerHTML) {
                    let foundText = '';
                    // Grab the same number of nodes that are in the searchText
                    for (let f = 0; f < feeLine.htmlElements.length; f++) {
                        foundText += this.pdfPgTextDivs[p - 1].textDivs[t + f].el.innerHTML;
                    }
                    if (searchText === foundText) {
                        foundIt = true;
                        start = t;
                    }
                }
                if (foundIt) break;
            }

            //////////////////////////////////////////////////////////////////////////
            // STEP 3: If this line of selected text is found, update it and the
            //         remaining number of textDivs selected with a selected status.
            //////////////////////////////////////////////////////////////////////////
            if (foundIt) {
                this.pdfPgHighlightedFeeLines.push({ pg: p, startTd: start, selCnt: feeLine.htmlElements.length });
                this.updateSelectedTextDivs(p, start, feeLine.htmlElements.length);
            } else {
                console.warn(`Could not find matching textDiv for highlighting: "${searchText}" on page ${p}`);
            }
        });
    }

    // Highlight the selected text elements
    // Be careful - this function uses 1-based numbering on page number - not zero-based
    updateSelectedTextDivs(pg: number, startTd: number, selCnt: number) {
        this.pdfTextHighlighted = true;
        const endTd = startTd + selCnt, n = 0;

        if ((pg === 0) || (endTd - startTd < 1)) return;

        for (let i = 0; i < selCnt; i++) {
            if (this.pdfPgTextDivs[pg - 1].textDivs[i + startTd].el.nodeName === 'SPAN') {
                // pg is a one-based number and the textDivs collection is zero-based
                this.pdfPgTextDivs[pg - 1].textDivs[i + startTd].selected = true;
                this.pdfPgTextDivs[pg - 1].textDivs[i + startTd].el.className = 'highlighted-text';
            }
        }
    }

    goToStmtReviewsList() {
        if (this.isSelfServiceReview) {
            this.router.navigate(['manage', 'self-service-reviews']);
        } else {
            this.router.navigate(['manage', 'admin', 'statement-reviews']);
        }
    }

    pdfZoomIn() {
        this.pdfZoom = Math.min(400, Math.round((this.pdfZoom + 10) / 10) * 10);
        this.pdfZoomInputValue = `${this.pdfZoom}%`;
    }

    pdfZoomOut() {
        this.pdfZoom = Math.max(20, Math.round((this.pdfZoom - 10) / 10) * 10);
        this.pdfZoomInputValue = `${this.pdfZoom}%`;
    }

    downloadPdf() {
        window.open(this.pdfSrc, '_blank');
    }

    onPdfZoomInputFocus(event: FocusEvent): void {
        this.pdfZoomInputValue = String(Math.round(this.pdfZoom));
        (event.target as HTMLInputElement).select();
    }

    onPdfZoomInputBlur(): void {
        const parsed = parseInt(this.pdfZoomInputValue, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
            const clamped = Math.min(Math.max(parsed, 20), 400);
            this.pdfZoom = clamped;
            this.pdfZoomInputValue = `${clamped}%`;
        } else {
            this.pdfZoomInputValue = `${Math.round(this.pdfZoom)}%`;
        }
    }

}
